import React, { useState, useEffect, useCallback } from 'react';
import { listTransactions, updateTransaction, cancelTransaction } from '../controllers/transactionsController';
import PosPrinter from '../components/PosPrinter';

const formatCurrency = (value) => {
  const amount = Number(value);
  if (Number.isNaN(amount)) return '₹0.00';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2
  }).format(amount);
};

const formatCurrencyNoDecimals = (value) => {
  const amount = Number(value);
  if (Number.isNaN(amount)) return '₹0';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Math.round(amount));
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function TransactionsPage() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);

  // Confirmation modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState(() => async () => {});
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmAllowAmountEdit, setConfirmAllowAmountEdit] = useState(false);
  const [confirmAmount, setConfirmAmount] = useState(0);
  const [confirmPayload, setConfirmPayload] = useState(null);
  const [confirmDiscount, setConfirmDiscount] = useState(0);
  // POS printer state for row-level printing
  const [posPrinterOpen, setPosPrinterOpen] = useState(false);
  const [posAutoPrint, setPosAutoPrint] = useState(false);
  const [posPrinterData, setPosPrinterData] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Filters state
  const [filters, setFilters] = useState({
    search: '',
    payment_source: 'all',
    payment_status: 'all',
    from: '',
    to: ''
  });

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        ...filters
      };
      
      // Remove empty filter values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === 'all') {
          delete params[key];
        }
      });
      
      const result = await listTransactions(params);
      setTransactions(result.data || []);
      setTotalItems(result.meta?.total || 0);
      setTotalPages(Math.ceil((result.meta?.total || 0) / itemsPerPage));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, filters]);

  const [stats, setStats] = useState({
    today: 0,
    monthly: 0,
    last30Days: 0,
    allTime: 0,
    loading: true
  });

  const fetchGlobalStats = useCallback(async () => {
    try {
      const result = await listTransactions({
        payment_status: 'paid',
        limit: 10000
      });
      const txs = result.data || [];
      
      const allTimeSum = txs.reduce((sum, tx) => sum + (Number(tx.total || 0) || 0), 0);

      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      const endOfToday = new Date();
      endOfToday.setHours(23, 59, 59, 999);

      const todaySum = txs.reduce((sum, tx) => {
        const created = tx.createdAt ? new Date(tx.createdAt) : null;
        if (created && created >= startOfToday && created <= endOfToday) {
          return sum + (Number(tx.total || 0) || 0);
        }
        return sum;
      }, 0);

      const startOfCurrentMonth = new Date();
      startOfCurrentMonth.setDate(1);
      startOfCurrentMonth.setHours(0, 0, 0, 0);

      const monthlySum = txs.reduce((sum, tx) => {
        const created = tx.createdAt ? new Date(tx.createdAt) : null;
        if (created && created >= startOfCurrentMonth) {
          return sum + (Number(tx.total || 0) || 0);
        }
        return sum;
      }, 0);

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      thirtyDaysAgo.setHours(0, 0, 0, 0);

      const thirtyDaysSum = txs.reduce((sum, tx) => {
        const created = tx.createdAt ? new Date(tx.createdAt) : null;
        if (created && created >= thirtyDaysAgo) {
          return sum + (Number(tx.total || 0) || 0);
        }
        return sum;
      }, 0);

      setStats({
        today: todaySum,
        monthly: monthlySum,
        last30Days: thirtyDaysSum,
        allTime: allTimeSum,
        loading: false
      });
    } catch (err) {
      console.error("Failed to fetch global stats:", err);
      setStats(prev => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    fetchGlobalStats();
  }, [fetchGlobalStats]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timeout = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timeout);
  }, [feedback]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      fetchTransactions();
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(parseInt(event.target.value));
    setCurrentPage(1);
  };

  const handleUpdateTransaction = async (transactionId, updateData) => {
    try {
      await updateTransaction(transactionId, updateData);
      setFeedback({ type: 'success', message: 'Transaction updated successfully' });
      await Promise.all([fetchTransactions(), fetchGlobalStats()]);
    } catch (err) {
      setFeedback({ type: 'error', message: err.message });
    }
  };

  const handleCancelTransaction = (transaction) => {
    setConfirmTitle('Confirm transaction cancellation');
    setConfirmMessage(
      `Are you sure you want to cancel this transaction?\n\nPlayer: ${transaction.player_name}\nAmount: ${formatCurrency(transaction.total)}`
    );
    // ensure any previous confirm state for payment edits doesn't interfere
    setConfirmAllowAmountEdit(false);
    setConfirmPayload(null);
    setConfirmAmount(0);
    setConfirmDiscount(0);

    setConfirmAction(() => async () => {
      try {
        await cancelTransaction(transaction._id);
        setFeedback({ type: 'success', message: 'Transaction cancelled' });
        await Promise.all([fetchTransactions(), fetchGlobalStats()]);
      } catch (err) {
        setFeedback({ type: 'error', message: err.message || 'Failed to cancel transaction' });
      }
    });
    setConfirmOpen(true);
  };

  const toggleGamePlayed = (transaction) => {
    const newValue = !transaction.gameplayed;
    setConfirmTitle('Confirm played status');
    setConfirmMessage(
      `Are you sure you want to ${newValue ? 'mark as Played' : 'mark as Pending'}?\n\nPlayer: ${transaction.player_name}\nAmount: ${formatCurrency(transaction.total)}`
    );
    setConfirmAction(() => async () => {
      await handleUpdateTransaction(transaction._id, { gameplayed: newValue });
    });
    setConfirmOpen(true);
  };

  const updatePaymentStatus = (transaction, newStatus) => {
    handleUpdateTransaction(transaction._id, { 
      payment_status: newStatus 
    });
  };

  const togglePaymentStatus = (transaction) => {
    const currentStatus = transaction.payment_status;
    const newStatus = currentStatus === 'paid' ? 'pending' : 'paid';
    const actionText = newStatus === 'paid' ? 'mark as paid' : 'mark as pending';

    setConfirmTitle('Confirm payment status change');
    setConfirmMessage(
      `Are you sure you want to ${actionText} this transaction?\n\nPlayer: ${transaction.player_name}\nAmount: ${formatCurrency(transaction.total)}\nCurrent Status: ${currentStatus}\nNew Status: ${newStatus}`
    );
    // allow editing amount when confirming payment status
    setConfirmAmount(Number(transaction.total || 0));
    setConfirmDiscount(0);
    setConfirmAllowAmountEdit(true);
    setConfirmPayload({ transactionId: transaction._id, newStatus });
    // also set a fallback action to update status without amount edit
    setConfirmAction(() => async () => {
      await handleUpdateTransaction(transaction._id, { payment_status: newStatus });
    });
    setConfirmOpen(true);
  };

  const refreshTransactions = () => {
    setFilters({
      search: '',
      payment_source: 'all',
      payment_status: 'all',
      from: '',
      to: ''
    });
    setCurrentPage(1);
    fetchTransactions();
  };

  // Confirmation modal helpers
  const confirmCancel = () => {
    setConfirmOpen(false);
    setConfirmMessage('');
    setConfirmTitle('');
    setConfirmAction(() => async () => {});
    setConfirmLoading(false);
  };

  const handleConfirm = async () => {
    setConfirmLoading(true);
    try {
      // if amount edit is allowed and we have a payload, call update with amount
      if (confirmAllowAmountEdit && confirmPayload) {
        const { transactionId, newStatus } = confirmPayload;
        const base = Number(confirmAmount || 0);
        const discount = Number(confirmDiscount || 0);
        const finalTotal = Math.max(0, +(base - discount).toFixed(2));
        await handleUpdateTransaction(transactionId, { payment_status: newStatus, total: finalTotal });
      } else {
        await confirmAction();
      }
      setConfirmOpen(false);
    } catch (err) {
      setFeedback({ type: 'error', message: err.message || 'Operation failed' });
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <div style={{ 
      width: '100%',
      minHeight: '100vh',
      padding: '32px 24px',
      boxSizing: 'border-box'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '40px' }}>
        <h1 style={{ 
          margin: 0, 
          color: 'var(--text-primary)', 
          fontSize: '2.5rem',
          fontWeight: '700',
          marginBottom: '12px'
        }}>
          Sales
        </h1>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginTop: '16px'
        }}>
          {/* Today's Sales Card */}
          <div style={{
            background: 'var(--card-bg)',
            border: '1px solid var(--card-border)',
            borderRadius: 'var(--border-radius-md)',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px' }}>Today's Sales</span>
            <span style={{ color: 'var(--accent)', fontSize: '1.4rem', fontWeight: '700' }}>
              {stats.loading ? '...' : formatCurrency(stats.today)}
            </span>
          </div>

          {/* This Month's Sales Card */}
          <div style={{
            background: 'var(--card-bg)',
            border: '1px solid var(--card-border)',
            borderRadius: 'var(--border-radius-md)',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px' }}>This Month's Sales</span>
            <span style={{ color: 'var(--text-primary)', fontSize: '1.4rem', fontWeight: '700' }}>
              {stats.loading ? '...' : formatCurrency(stats.monthly)}
            </span>
          </div>

          {/* Last 30 Days Sales Card */}
          <div style={{
            background: 'var(--card-bg)',
            border: '1px solid var(--card-border)',
            borderRadius: 'var(--border-radius-md)',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px' }}>Last 30 Days Sales</span>
            <span style={{ color: 'var(--text-primary)', fontSize: '1.4rem', fontWeight: '700' }}>
              {stats.loading ? '...' : formatCurrency(stats.last30Days)}
            </span>
          </div>

          {/* All-time Sales Card */}
          <div style={{
            background: 'var(--card-bg)',
            border: '1px solid var(--card-border)',
            borderRadius: 'var(--border-radius-md)',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px' }}>All-Time Sales</span>
            <span style={{ color: 'var(--text-primary)', fontSize: '1.4rem', fontWeight: '700' }}>
              {stats.loading ? '...' : formatCurrency(stats.allTime)}
            </span>
          </div>
        </div>
      </div>

      {/* Feedback Messages */}
      {feedback && (
        <div style={{
          padding: '16px 20px',
          borderRadius: 'var(--border-radius-sm)',
          marginBottom: '32px',
          background: feedback.type === 'error' ? 'rgba(255, 99, 99, 0.1)' : 'rgba(0, 229, 255, 0.1)',
          border: `1px solid ${feedback.type === 'error' ? 'rgba(255, 99, 99, 0.2)' : 'rgba(0, 229, 255, 0.2)'}`,
          color: feedback.type === 'error' ? '#ff6363' : 'var(--accent)',
          fontSize: '1rem',
          fontWeight: '500'
        }}>
          {feedback.message}
        </div>
      )}

      {error && (
        <div style={{
          padding: '16px 20px',
          borderRadius: 'var(--border-radius-sm)',
          marginBottom: '32px',
          background: 'rgba(255, 99, 99, 0.1)',
          border: '1px solid rgba(255, 99, 99, 0.2)',
          color: '#ff6363',
          fontSize: '1rem',
          fontWeight: '500'
        }}>
          {error}
        </div>
      )}

      {/* Filters */}
      {/* <div style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--border-radius-lg)',
        padding: '32px',
        marginBottom: '32px',
        boxShadow: 'var(--neon-glow)',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '24px',
          marginBottom: '24px'
        }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '12px', 
              color: 'var(--text-primary)',
              fontWeight: '600',
              fontSize: '1rem'
            }}>
              Search
            </label>
            <input
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search by name or phone"
              style={{
                width: '100%',
                padding: '16px 20px',
                background: 'var(--input-bg)',
                border: '1px solid var(--card-border)',
                borderRadius: 'var(--border-radius-sm)',
                color: 'var(--text-primary)',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '12px', 
              color: 'var(--text-primary)',
              fontWeight: '600',
              fontSize: '1rem'
            }}>
              Payment Source
            </label>
            <select 
              name="payment_source" 
              value={filters.payment_source} 
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '16px 20px',
                background: 'var(--input-bg)',
                border: '1px solid var(--card-border)',
                borderRadius: 'var(--border-radius-sm)',
                color: 'var(--text-primary)',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            >
              <option value="all">All Sources</option>
              <option value="cash">Cash</option>
              <option value="online">Online</option>
            </select>
          </div>

          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '12px', 
              color: 'var(--text-primary)',
              fontWeight: '600',
              fontSize: '1rem'
            }}>
              Payment Status
            </label>
            <select 
              name="payment_status" 
              value={filters.payment_status} 
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '16px 20px',
                background: 'var(--input-bg)',
                border: '1px solid var(--card-border)',
                borderRadius: 'var(--border-radius-sm)',
                color: 'var(--text-primary)',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            >
              <option value="all">All Statuses</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '12px', 
              color: 'var(--text-primary)',
              fontWeight: '600',
              fontSize: '1rem'
            }}>
              From Date
            </label>
            <input
              name="from"
              type="datetime-local"
              value={filters.from}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '16px 20px',
                background: 'var(--input-bg)',
                border: '1px solid var(--card-border)',
                borderRadius: 'var(--border-radius-sm)',
                color: 'var(--text-primary)',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '12px', 
              color: 'var(--text-primary)',
              fontWeight: '600',
              fontSize: '1rem'
            }}>
              To Date
            </label>
            <input
              name="to"
              type="datetime-local"
              value={filters.to}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '16px 20px',
                background: 'var(--input-bg)',
                border: '1px solid var(--card-border)',
                borderRadius: 'var(--border-radius-sm)',
                color: 'var(--text-primary)',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '16px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={refreshTransactions}
            disabled={loading}
            style={{
              padding: '14px 28px',
              background: 'var(--card-bg)',
              color: 'var(--text-primary)',
              border: '1px solid var(--card-border)',
              borderRadius: 'var(--border-radius-sm)',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600',
              transition: 'all 0.2s ease'
            }}
          >
            Clear Filters
          </button>
          <button
            type="button"
            onClick={fetchTransactions}
            disabled={loading}
            style={{
              padding: '14px 28px',
              background: 'var(--accent)',
              color: '#0b1735',
              border: 'none',
              borderRadius: 'var(--border-radius-sm)',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600',
              transition: 'all 0.2s ease'
            }}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div> */}

      {/* Results Summary */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        padding: '0 12px'
      }}>
        <div style={{ color: 'var(--text-secondary)', fontSize: '1.1rem', fontWeight: '500' }}>
          Showing {transactions.length} of {totalItems} transactions
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <label style={{ color: 'var(--text-secondary)', fontSize: '1rem', fontWeight: '500' }}>
            Items per page:
          </label>
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            style={{
              padding: '10px 16px',
              background: 'var(--input-bg)',
              border: '1px solid var(--card-border)',
              borderRadius: 'var(--border-radius-sm)',
              color: 'var(--text-primary)',
              fontSize: '1rem',
              fontWeight: '500'
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {/* Transactions Table */}
      <div style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--border-radius-lg)',
        overflow: 'hidden',
        boxShadow: 'var(--neon-glow)',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {loading ? (
          <div style={{
            padding: '80px 40px',
            textAlign: 'center',
            color: 'var(--text-secondary)',
            fontSize: '1.2rem',
            fontWeight: '500'
          }}>
            Loading transactions...
          </div>
        ) : transactions.length === 0 ? (
          <div style={{
            padding: '80px 40px',
            textAlign: 'center',
            color: 'var(--text-secondary)',
            fontSize: '1.2rem',
            fontWeight: '500'
          }}>
            No transactions found
          </div>
        ) : (
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            margin: 0
          }}>
            <thead>
              <tr style={{
                background: 'rgba(255, 255, 255, 0.02)',
                borderBottom: '1px solid var(--card-border)'
              }}>
                <th style={{ padding: '20px 24px', textAlign: 'left', color: 'var(--text-primary)', fontWeight: '700', fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Invoice</th>
                <th style={{ padding: '20px 24px', textAlign: 'left', color: 'var(--text-primary)', fontWeight: '700', fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Player</th>
                <th style={{ padding: '20px 24px', textAlign: 'left', color: 'var(--text-primary)', fontWeight: '700', fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Items</th>
                <th style={{ padding: '20px 24px', textAlign: 'right', color: 'var(--text-primary)', fontWeight: '700', fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total</th>
                <th style={{ padding: '20px 24px', textAlign: 'center', color: 'var(--text-primary)', fontWeight: '700', fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</th>
                <th style={{ padding: '20px 24px', textAlign: 'center', color: 'var(--text-primary)', fontWeight: '700', fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Played</th>
                <th style={{ padding: '20px 24px', textAlign: 'center', color: 'var(--text-primary)', fontWeight: '700', fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Actions</th>
                <th style={{ padding: '20px 24px', textAlign: 'left', color: 'var(--text-primary)', fontWeight: '700', fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((transaction, index) => (
                <tr 
                  key={transaction._id} 
                  style={{ 
                    borderBottom: index < transactions.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none'
                  }}
                >
                  <td style={{ padding: '20px 24px', color: 'var(--text-primary)', fontSize: '1rem', fontWeight: '500' }}>
                    {transaction.invoice_number || 'N/A'}
                  </td>
                  <td style={{ padding: '20px 24px' }}>
                    <div>
                      <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '1rem' }}>
                        {transaction.player_name}
                      </div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '4px' }}>
                        {transaction.phone}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '20px 24px' }}>
                    <div style={{ color: 'var(--text-primary)', fontSize: '1rem', fontWeight: '500' }}>
                      {transaction.items?.length || 0} item(s)
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '4px' }}>
                      {transaction.items?.map(item => item.name).join(', ') || 'N/A'}
                    </div>
                  </td>
                  <td style={{ padding: '20px 24px', textAlign: 'right', color: 'var(--text-primary)', fontWeight: '700', fontSize: '1.1rem' }}>
                    {formatCurrencyNoDecimals(transaction.total)}
                  </td>
                  <td style={{ padding: '20px 24px', textAlign: 'center' }}>
                    <button
                      onClick={() => togglePaymentStatus(transaction)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        borderRadius: '20px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        background: transaction.payment_status === 'paid' ? 'rgba(0, 229, 255, 0.2)' : 'rgba(255, 193, 7, 0.2)',
                        color: transaction.payment_status === 'paid' ? 'var(--accent)' : '#ffc107',
                        border: `1px solid ${transaction.payment_status === 'paid' ? 'rgba(0, 229, 255, 0.3)' : 'rgba(255, 193, 7, 0.3)'}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.05)';
                        e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      {transaction.payment_status === 'paid' ? '✓' : '⏳'} {transaction.payment_status}
                    </button>
                    <div style={{ marginTop: '8px', fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500' }}>
                      {transaction.payment_source}
                    </div>
                  </td>
                  <td style={{ padding: '20px 24px', textAlign: 'center' }}>
                    <button
                      onClick={() => toggleGamePlayed(transaction)}
                      style={{
                        padding: '10px 16px',
                        background: transaction.gameplayed ? 'rgba(0, 229, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                        color: transaction.gameplayed ? 'var(--accent)' : 'var(--text-secondary)',
                        border: `1px solid ${transaction.gameplayed ? 'rgba(0, 229, 255, 0.3)' : 'var(--card-border)'}`,
                        borderRadius: 'var(--border-radius-sm)',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {transaction.gameplayed ? '✓ Played' : '⏳ Pending'}
                    </button>
                  </td>
                  <td style={{ padding: '20px 24px', textAlign: 'center', display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={() => {
                        // prepare data for PosPrinter and open the print dialog UI (do not auto-print)
                        const items = (transaction.items || []).map((it) => ({ name: it.name, duration: it.duration || '', price: it.price || 0 }));
                        setPosPrinterData({ cartItems: items, userData: { name: transaction.player_name, phone: transaction.phone }, total: transaction.total, subtotal: transaction.total, tax: 0, transactionId: transaction._id });
                        setPosAutoPrint(false);
                        setPosPrinterOpen(true);
                      }}
                      style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'transparent', cursor: 'pointer' }}
                      title="Print receipt"
                    >
                      🖨️
                    </button>

                    <button
                      type="button"
                      onClick={() => handleCancelTransaction(transaction)}
                      style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(255,0,0,0.12)', background: 'transparent', color: '#ff4d4f', cursor: 'pointer' }}
                      title="Cancel invoice"
                    >
                      ❌
                    </button>
                  </td>
                  <td style={{ padding: '20px 24px', color: 'var(--text-secondary)', fontSize: '0.9rem', fontWeight: '500' }}>
                    {formatDate(transaction.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '12px',
          marginTop: '40px',
          padding: '24px'
        }}>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              padding: '12px 20px',
              background: currentPage === 1 ? 'var(--input-bg)' : 'var(--card-bg)',
              color: currentPage === 1 ? 'var(--text-secondary)' : 'var(--text-primary)',
              border: '1px solid var(--card-border)',
              borderRadius: 'var(--border-radius-sm)',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: '600',
              transition: 'all 0.2s ease'
            }}
          >
            Previous
          </button>
          
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
            if (pageNum > totalPages) return null;
            
            return (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                style={{
                  padding: '12px 20px',
                  background: pageNum === currentPage ? 'var(--accent)' : 'var(--card-bg)',
                  color: pageNum === currentPage ? '#0b1735' : 'var(--text-primary)',
                  border: '1px solid var(--card-border)',
                  borderRadius: 'var(--border-radius-sm)',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: pageNum === currentPage ? '700' : '600',
                  transition: 'all 0.2s ease'
                }}
              >
                {pageNum}
              </button>
            );
          })}
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{
              padding: '12px 20px',
              background: currentPage === totalPages ? 'var(--input-bg)' : 'var(--card-bg)',
              color: currentPage === totalPages ? 'var(--text-secondary)' : 'var(--text-primary)',
              border: '1px solid var(--card-border)',
              borderRadius: 'var(--border-radius-sm)',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: '600',
              transition: 'all 0.2s ease'
            }}
          >
            Next
          </button>
        </div>
      )}

      {/* POS Printer Modal (row-level printing) */}
      <PosPrinter
        isOpen={posPrinterOpen}
        onClose={() => {
          setPosPrinterOpen(false);
          setPosAutoPrint(false);
          setPosPrinterData(null);
        }}
        cartItems={posPrinterData ? posPrinterData.cartItems : []}
        userData={posPrinterData ? posPrinterData.userData : { name: '', phone: '' }}
        total={posPrinterData ? posPrinterData.total : 0}
        subtotal={posPrinterData ? posPrinterData.subtotal : 0}
        tax={posPrinterData ? posPrinterData.tax : 0}
        transactionId={posPrinterData ? posPrinterData.transactionId : undefined}
        onPrintComplete={(printStatus) => {
          setPosAutoPrint(false);
          setPosPrinterOpen(false);
          setPosPrinterData(null);
          setFeedback({ type: 'success', message: 'Receipt printed' });
        }}
        autoPrintNow={posAutoPrint}
        backdropZIndex={10001}
      />

      {/* Confirmation Modal */}
      {confirmOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            width: '100%',
            maxWidth: 520,
            background: 'var(--card-bg)',
            border: '1px solid var(--card-border)',
            borderRadius: '12px',
            padding: 20,
            boxShadow: 'var(--neon-glow)'
          }}>
            <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>{confirmTitle}</h3>
            <p style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', marginTop: 12 }}>{confirmMessage}</p>
            {confirmAllowAmountEdit && (
              <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 160 }}>
                  <label style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Amount</label>
                  <input type="number" value={confirmAmount} onChange={(e) => setConfirmAmount(Number(e.target.value))} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--input-bg)', color: 'var(--text-primary)', width: '100%' }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 140 }}>
                  <label style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Discount</label>
                  <input type="number" value={confirmDiscount} onChange={(e) => setConfirmDiscount(Number(e.target.value))} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--input-bg)', color: 'var(--text-primary)', width: '100%' }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 140 }}>
                  <label style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Final</label>
                  <div style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontWeight: 700 }}>{formatCurrency(Math.max(0, +(Number(confirmAmount || 0) - Number(confirmDiscount || 0)).toFixed(2)))}</div>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 18 }}>
              <button type="button" onClick={confirmCancel} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--card-border)', borderRadius: 8, color: 'var(--text-secondary)', cursor: 'pointer' }}>Cancel</button>
              <button type="button" onClick={handleConfirm} disabled={confirmLoading} style={{ padding: '8px 16px', background: 'var(--accent)', border: 'none', borderRadius: 8, color: '#0b1735', fontWeight: 600, cursor: 'pointer' }}>{confirmLoading ? 'Processing...' : 'Confirm'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
